import { cn } from '@heidi/utils';
import { ImageIcon, Paperclip } from 'lucide-react';
import { useIntl } from 'react-intl';

import { composerFileDropOverlayMessages } from './ComposerFileDropOverlay.messages';

import type { JSX } from 'react';

interface ComposerFileDropOverlayProps {
  visible: boolean;
  className?: string;
  testId?: string;
}

/**
 * Full-surface drop affordance for Work composers. Pointer-events-none
 * so the underlying shell keeps receiving dragenter/leave events.
 */
export const ComposerFileDropOverlay = ({
  visible,
  className,
  testId = 'composer-file-drop-overlay',
}: ComposerFileDropOverlayProps): JSX.Element | null => {
  const intl = useIntl();

  if (!visible) return null;

  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-surface/90 backdrop-blur-[2px] duration-150 animate-in fade-in',
        className
      )}
      data-testid={testId}
      aria-hidden
    >
      <div className="absolute inset-[3px] rounded-[inherit] border-2 border-dashed border-border-strong" />
      <div className="relative flex max-w-sm flex-col items-center gap-2 px-6 text-center">
        <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
          <Paperclip className="size-4 shrink-0" aria-hidden />
          {intl.formatMessage(composerFileDropOverlayMessages.dropTitle)}
        </div>
        <p className="flex items-center justify-center gap-1.5 text-xs text-text-secondary">
          <ImageIcon className="size-3.5 shrink-0 opacity-70" aria-hidden />
          {intl.formatMessage(composerFileDropOverlayMessages.dropHint)}
        </p>
      </div>
    </div>
  );
};
